import { createContext, useContext, useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useCurrentDate } from 'hooks/date-hook';
import { useSnackbar } from "providers/SnackbarProvider";
import { useHttpClient } from 'hooks/http-hook';
import { useAuth } from "providers/AuthProvider";
import { calculateHoursFromTotalMinutes, calculateRemainingMinutesFromTotalMinutes, resolveFiles } from "utils";

const EditDayContext = createContext();

export const EditDayProvider = ({ children }) => {
  const { token } = useAuth();
  const showSnackbar = useSnackbar();
  const today = useCurrentDate();
  const { sendRequest } = useHttpClient();
  const { date: datePassedInFromUrl } = useParams();
  const location = useLocation();
  const isEditPage = location.pathname.includes('edit');
  const date = datePassedInFromUrl ? new Date(...datePassedInFromUrl.split('-').map((v, i) => i === 1 ? v - 1 : Number(v))) : today;
  const [loadedDay, setLoadedDay] = useState();
  const [tagsList, setTagsList] = useState([]);
  const [files, setFiles] = useState([]);
  const setters = { tag: setTagsList };

  const [dayDetailsEdit, setDayDetailsEdit] = useState({
    additionalFiles: [],
    addToTimeline: false,
    almanac: '',
    almanacIsHtml: false,
    bj: '',
    bloodPressureHigh: '',
    bloodPressureLow: '',
    bmi: '',
    bmr: '',
    bodyFat: '',
    bodyWater: '',
    boneMass: '',
    caloriesBurned: '',
    cholesterol: '',
    date: date,
    dateCreated: '',
    description: '',
    descriptionIsHtml: false,
    dietNotes: '',
    dietScore: '',
    drink: '',
    exerciseHours: '',
    exerciseMinutes: '',
    exerciseNotes: '',
    extras: '',
    fatFreeBodyWeight: '',
    files: [],
    filesFlaggedForDeletion: [],
    gratitude: '',
    hoursAsleep: '',
    hoursCoreSleep: '',
    hoursDeepSleep: '',
    hoursRemSleep: '',
    ironCount: '',
    massage: false,
    medical: '',
    medicalIsHtml: false,
    meditation: '',
    metabolicAge: '',
    minutesAsleep: '',
    minutesCoreSleep: '',
    minutesDeepSleep: '',
    minutesRemSleep: '',
    moneySpent: '',
    muscleMass: '',
    musicHours: '',
    musicMinutes: '',
    protein: '',
    restingHeartRate: '',
    sex: '',
    skeletalMuscle: '',
    steps: '',
    subcutaneousFat: '',
    tagsList: [],
    timelineFiles: [],
    timelineDescription: '',
    timelineEndDate: '',
    timelineIframeSrc: '',
    timelineSpanToDate: false,
    timelineSpanToPresent: false,
    timelineTagsList: [],
    timelineTitle: '',
    useDayFilesForTimeline: true,
    useDayDescriptionForTimeline: false,
    useDayTagsForTimeline: true,
    visceralFat: '',
    watched: [],
    water: '',
    weight: '',
    mostRecentDayRecorded: null,
  });

  const findMostRecentDayRecorded = (days) => {
    const index = days.findIndex(x => x.dateString === datePassedInFromUrl);
    if (index <= 0) return null;
    return days[index - 1];
  };

  const normalizeDay = (day, days) => ({
    ...day,
    additionalFiles: [],
    addToTimeline: day.addToTimeline ?? false,
    almanac: day.almanac ?? '',
    almanacIsHtml: day.almanacIsHtml ?? false,
    bj: day.bj ?? '',
    bloodPressureHigh: day.bloodPressureHigh ?? '',
    bloodPressureLow: day.bloodPressureLow ?? '',
    bmi: day.bmi ?? '',
    bmr: day.bmr ?? '',
    bodyFat: day.bodyFat ?? '',
    bodyWater: day.bodyWater ?? '',
    boneMass: day.boneMass ?? '',
    caloriesBurned: day.caloriesBurned ?? '',
    cholesterol: day.cholesterol ?? '',
    date: day.date ?? date,
    dateCreated: day.dateCreated ?? '',
    description: day.description ?? '',
    descriptionIsHtml: day.descriptionIsHtml ?? false,
    dietNotes: day.dietNotes ?? '',
    dietScore: day.dietScore ?? '',
    drink: day.drink ?? '',
    exerciseHours: day.exerciseMinutes !== null ? calculateHoursFromTotalMinutes(day.exerciseMinutes) : '',
    exerciseMinutes: day.exerciseMinutes !== null ? calculateRemainingMinutesFromTotalMinutes(day.exerciseMinutes) : '',
    exerciseNotes: day.exerciseNotes ?? '',
    extras: day.extras ?? '',
    fatFreeBodyWeight: day.fatFreeBodyWeight ?? '',
    files: day.files ?? [],
    filesFlaggedForDeletion: [],
    gratitude: day.gratitude ?? '',
    hoursAsleep: day.minutesAsleep !== null ? calculateHoursFromTotalMinutes(day.minutesAsleep) : '',
    hoursCoreSleep: day.minutesCoreSleep !== null ? calculateHoursFromTotalMinutes(day.minutesCoreSleep) : '',
    hoursDeepSleep: day.minutesDeepSleep !== null ? calculateHoursFromTotalMinutes(day.minutesDeepSleep) : '',
    hoursRemSleep: day.minutesRemSleep !== null ? calculateHoursFromTotalMinutes(day.minutesRemSleep) : '',
    ironCount: day.ironCount ?? '',
    massage: day.massage ?? false,
    medical: day.medical ?? '',
    medicalIsHtml: day.medicalIsHtml ?? false,
    meditation: day.meditation ?? '',
    metabolicAge: day.metabolicAge ?? '',
    minutesAsleep: day.minutesAsleep !== null ? calculateRemainingMinutesFromTotalMinutes(day.minutesAsleep) : '',
    minutesCoreSleep: day.minutesCoreSleep !== null ? calculateRemainingMinutesFromTotalMinutes(day.minutesCoreSleep) : '',
    minutesDeepSleep: day.minutesDeepSleep !== null ? calculateRemainingMinutesFromTotalMinutes(day.minutesDeepSleep) : '',
    minutesRemSleep: day.minutesRemSleep !== null ? calculateRemainingMinutesFromTotalMinutes(day.minutesRemSleep) : '',
    moneySpent: day.moneySpent ?? '',
    muscleMass: day.muscleMass ?? '',
    musicHours: day.musicMinutes !== null ? calculateHoursFromTotalMinutes(day.musicMinutes) : '',
    musicMinutes: day.musicMinutes !== null ? calculateRemainingMinutesFromTotalMinutes(day.musicMinutes) : '',
    protein: day.protein ?? '',
    restingHeartRate: day.restingHeartRate ?? '',
    sex: day.sex ?? '',
    skeletalMuscle: day.skeletalMuscle ?? '',
    steps: day.steps ?? '',
    subcutaneousFat: day.subcutaneousFat ?? '',
    tagsList: day.tagsList ?? [],
    timelineFiles: day.timelineFiles ?? [],
    timelineDescription: day.timelineDescription ?? '',
    timelineEndDate: day.timelineEndDate ?? '',
    timelineIframeSrc: day.timelineIframeSrc ?? '',
    timelineSpanToDate: day.timelineSpanToDate ?? false,
    timelineSpanToPresent: day.timelineSpanToPresent ?? false,
    timelineTagsList: day.timelineTagsList ?? [],
    timelineTitle: day.timelineTitle ?? '',
    useDayFilesForTimeline: day.useDayFilesForTimeline ?? true,
    useDayDescriptionForTimeline: day.useDayDescriptionForTimeline ?? false,
    useDayTagsForTimeline: day.useDayTagsForTimeline ?? true,
    visceralFat: day.visceralFat ?? '',
    watched: day.watched ?? [],
    water: day.water ?? '',
    weight: day.weight ?? '',
    mostRecentDayRecorded: findMostRecentDayRecorded(days),
  });

  useEffect(() => {
    const fetchDays = async () => {
      try {
        const response = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/days/${datePassedInFromUrl}`, 'GET', null, {Authorization: 'Bearer ' + token});
        const days = response.days;
        return days;
      } catch (err) {
        console.error(err);
        showSnackbar('Error fetching.');
        return null;
      }
    };

    const getCenterDayAndFiles = async (days) => {
      const day = days.find(x => x.dateString === datePassedInFromUrl);
      const resolvedFiles = await resolveFiles(day.files);
      setLoadedDay(day); // used for deletion
      setDayDetailsEdit(normalizeDay(day, days));
      setTagsList(day.tags);
      setFiles(resolvedFiles);
    }

    if (isEditPage) {
      fetchDays().then((days) => {
        if (!days) return;
        getCenterDayAndFiles(days);
      });
    }
  }, [sendRequest, datePassedInFromUrl, isEditPage, showSnackbar, token]);  

  const flagAttachmentForDeletionHandler = (e, fileName) => {
    let attachment = e.target.closest('.attachments__attachment');
    const isFlagged = attachment.classList.contains('attachments__attachment--flagged-for-deletion');
    const updatedFiles = isFlagged ? dayDetailsEdit.filesFlaggedForDeletion.filter(x => x !== fileName) : [...dayDetailsEdit.filesFlaggedForDeletion, fileName];
    setDayDetailsEdit(prev => ({...prev, filesFlaggedForDeletion: updatedFiles}))
  }

  return (
    <EditDayContext.Provider value={{ 
      loadedDay, 
      dayDetailsEdit, 
      setDayDetailsEdit, 
      tagsList, 
      setters, 
      flagAttachmentForDeletionHandler,
      files,
      showSnackbar,
    }}>
      {children}
    </EditDayContext.Provider>
  );
};

export const useEditDayContext = () => useContext(EditDayContext);
